import React, { createContext, useCallback, useEffect, useState, useRef } from 'react';
import connectionStatus, { SERVER_REACHABLE } from '../connectionStatus';
import * as Sentry from "@sentry/react";
import { useLocalStorage } from './hooks';
import { useDispatch } from 'react-redux';
import { ActionCreators } from '../state/actions';
import { createSession } from '../state/asyncActions';
import { markEncryptedStorageReady } from './encryptedStorage';
import {
  hasSessionDek,
  setSessionDek,
  clearSessionDek,
  getSessionDekAsCryptoKey,
  generateDek,
  findLegacyDek,
  removeLegacyKeys,
  getPrfWrap,
  wrapDekWithPrf,
  setPrfWrap,
  serverWrapDek,
  getKeyStatus,
  unlockDek,
} from './keyManager';
import { initializeDatabase, closeDatabase, deleteDatabase, SyncEngine, isDatabaseReady } from '../db';
import { migrateLegacyData } from '../db/index.js';
import { fetchAllCases, triggerSync } from '../db/actions';
import axios from 'axios';

const defaultAuthContext = {
  data: {},
  isAuthenticated: false,
  authDate: '',
  initStage: '',
  isAppReady: false,
  initError: null,
  retryInit: () => {},
  setData: () => {},
  createSession: async () => {},
  updateSession: () => {},
  refreshSession: async () => {},
  removeSession: () => {},
};

const AuthContext = createContext(defaultAuthContext);

/**
 * Ensures the DEK is in sessionStorage and the per-user Dexie database is open.
 *
 * Upgrade path (two-source migration):
 *   1. Check jb_enc_key_<emailHash> in localStorage  — post-logout path
 *   2. Check jb_enc_key in sessionStorage             — active-session-across-deploy path
 *   If either found, adopt the bare DEK, wrap it on the server, remove legacy entries.
 *
 * Fresh-key path (no legacy key, server has no dekWrapped):
 *   Delete any stale Dexie database (encrypted with an unrecoverable key),
 *   generate a new DEK, and wrap it on the server.
 *
 * Normal unlock path (server has dekWrapped):
 *   Delegate to keyManager.unlockDek — tries PRF first, falls back to server unwrap.
 *
 * prfParams: { prfOutput, prfCredentialId, prfWrappedDek, prfSalt } — all optional.
 */
async function ensureEncryptionKey(userEmail, prfParams = null, setStage = () => {}) {
  if (!userEmail) return;

  setStage('Verifying session...');

  if (!hasSessionDek()) {
    // --- Migration check ---
    const legacy = await findLegacyDek(userEmail);

    if (legacy) {
      // Adopt the existing DEK so Dexie data stays valid
      setSessionDek(legacy.dek);
      let wrapOk = false;
      try {
        await serverWrapDek(legacy.dek, false);
        wrapOk = true;
      } catch (e) {
        if (e?.response?.status === 409) {
          // Already wrapped from a previous partial run — treat as success
          wrapOk = true;
        } else {
          // Wrap failed (server error, bad key, etc.). Leave the legacy key in
          // place so the migration retries cleanly on the next login rather than
          // leaving the user with no recovery path.
          console.warn('serverWrapDek during migration failed — will retry on next login:', e);
        }
      }
      if (wrapOk) {
        await removeLegacyKeys(legacy.emailHash);
        console.log('Migrated legacy DEK to server-wrapped envelope');
      }
    } else {
      // --- No legacy key: check server ---
      let status;
      try {
        status = await getKeyStatus();
      } catch (e) {
        throw new Error('Could not reach server to unlock local data. Please check your connection and try again.');
      }

      if (status.hasServerWrappedDek) {
        // Normal unlock: server unwrap or PRF
        await unlockDek(prfParams || {});
      } else {
        // Key is genuinely gone (new user, or storage fully wiped).
        // Verify the server can accept a wrap BEFORE deleting local data —
        // if serverWrapDek will fail (bad server key, unreachable), we want
        // to surface the error rather than silently wipe the DB every login.
        const dek = await generateDek();
        await serverWrapDek(dek, false); // throws if server rejects — intentional

        // Wrap succeeded: safe to wipe any stale Dexie rows (encrypted with an
        // unrecoverable key) and open a fresh database.
        await deleteDatabase(userEmail).catch(e =>
          console.warn('Failed to delete stale local database:', e));

        setSessionDek(dek);
        console.log('Generated new DEK and wrapped on server');
      }
    }
  }

  // DEK is now in sessionStorage — allow encryptedStorage reads
  markEncryptedStorageReady();

  // Register a PRF wrap for this credential if we have a PRF output but no cached wrap yet
  if (prfParams?.prfOutput && prfParams?.prfCredentialId) {
    const existing = getPrfWrap(prfParams.prfCredentialId);
    if (!existing && prfParams.prfSalt) {
      try {
        const dek = getSessionDek();
        const wrap = await wrapDekWithPrf(dek, prfParams.prfOutput, prfParams.prfSalt);
        setPrfWrap(prfParams.prfCredentialId, wrap);
        axios.put(
          `/api/webauthn/credentials/${prfParams.prfCredentialId}/prf-wrap`,
          { prfWrappedDek: { ciphertext: wrap.ciphertext, iv: wrap.iv } },
          { withCredentials: true }
        ).catch(e => console.warn('Could not upload PRF wrap to server (non-fatal):', e));
      } catch (e) {
        console.warn('PRF wrap registration failed (non-fatal):', e);
      }
    }
  }

  // Open the per-user Dexie database
  try {
    setStage('Loading local data...');
    const encryptionKey = await getSessionDekAsCryptoKey();
    await initializeDatabase(userEmail, encryptionKey);
    console.log('Per-user database initialized');

    setStage('Checking for data updates...');
    try {
      const migrationResult = await migrateLegacyData();
      if (migrationResult.success && (migrationResult.casesMigrated || migrationResult.jurorsMigrated)) {
        console.log('Legacy data migration complete:', migrationResult);
      }
    } catch (migrationError) {
      console.error('Migration failed (non-fatal):', migrationError);
    }

    setStage('Syncing with server...');
    await SyncEngine.initialize();
    console.log('Sync engine initialized');
  } catch (error) {
    console.error('Failed to initialize database:', error);
  }
}

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setAuthenticated] = useLocalStorage(false, 'isAuthenticated');
  const [data, setData] = useLocalStorage({}, 'user');
  const [authDate, setAuthDate] = useLocalStorage('', 'auth-expiration');

  const dispatch = useDispatch();

  const [initStage, setInitStage] = useState('');
  const [isAppReady, setIsAppReady] = useState(false);
  const [initError, setInitError] = useState(null);
  const initStarted = useRef(false);

  // Run (or re-run) app initialization for an already-authenticated user.
  // On failure the app still renders — local data stays locked — but the
  // failure is surfaced via initError and retried when the server returns.
  const runInit = useCallback((email) => {
    initStarted.current = true;
    setInitStage('Verifying session...');
    return ensureEncryptionKey(email, null, setInitStage)
      .then(() => {
        setInitError(null);
        setInitStage('');
        setIsAppReady(true);
      })
      .catch((error) => {
        console.error('App initialization failed:', error);
        initStarted.current = false;
        setInitStage('');
        setInitError(error?.message || 'Could not unlock your local data.');
        setIsAppReady(true);
      });
  }, []);

  useEffect(() => {
    if (isAuthenticated && data?.email) {
      Sentry.setUser({ email: data.email, username: data.name });
    } else {
      Sentry.setUser(null);
    }
  }, [isAuthenticated, data?.id, data?.email, data?.name]);

  // Initialize for already-logged-in users (page reload) — no PRF output available here
  useEffect(() => {
    if (!isAuthenticated || !data?.email) return;

    // After Vite HMR, the db module singleton can be reset while this ref still
    // says init completed — allow a one-time re-init to reopen local data.
    if (initStarted.current && !isDatabaseReady()) {
      console.log('Database not ready after module reload; re-initializing...');
      initStarted.current = false;
    }

    if (initStarted.current) return;

    runInit(data.email);
  }, [isAuthenticated, data?.email, runInit]);

  // When initialization failed (e.g. server unreachable on reload, so the
  // local data couldn't be unlocked), retry automatically once the server
  // becomes reachable again. connectionStatus polls while unreachable, so
  // this also covers "backend was down, network was fine".
  useEffect(() => {
    if (!initError) return undefined;

    const retryWhenReachable = () => {
      if (!initStarted.current && isAuthenticated && data?.email) {
        console.log('Server reachable again — retrying app initialization...');
        runInit(data.email);
      }
    };

    connectionStatus.on(SERVER_REACHABLE, retryWhenReachable);
    return () => connectionStatus.removeListener(SERVER_REACHABLE, retryWhenReachable);
  }, [initError, isAuthenticated, data?.email, runInit]);

  return (
    <AuthContext.Provider
      value={{
        data,
        isAuthenticated,
        authDate,
        initStage,
        isAppReady,
        initError,
        retryInit: () => {
          if (!initStarted.current && isAuthenticated && data?.email) {
            return runInit(data.email);
          }
          return Promise.resolve();
        },
        setData,
        createSession: async (sessionData, expires, prfParams = null) => {
          console.log('Creating session...');

          setInitStage('Verifying session...');
          await ensureEncryptionKey(sessionData.email, prfParams, setInitStage);

          setData(sessionData);
          setAuthenticated(true);
          setAuthDate(expires);
          initStarted.current = true;
          setIsAppReady(true);
          setInitStage('');
          dispatch(createSession(sessionData, expires));
        },
        updateSession: (updates) => {
          const copy = { ...data };
          for (const name of Object.keys(updates)) {
            copy[name] = updates[name];
          }
          console.log('Setting user data:', copy);
          setData(copy);
        },
        refreshSession: async () => {
          try {
            console.log('Refreshing session from server...');
            const result = await axios.get('/api/user');
            if (result?.data) {
              const userData = result.data;
              const updatedData = {
                ...data,
                name: userData.name,
                email: userData.email,
                groupsLastUpdated: userData.groupsLastUpdated,
                mfaEnabled: userData.mfaEnabled,
                companyRequiresMfa: userData.companyRequiresMfa,
                preferredMfa: userData.preferredMfa,
                subscriptionStatus: userData.subscriptionStatus,
              };
              setData(updatedData);
              dispatch(ActionCreators.updateSessionDataPoint('subscriptionStatus', userData.subscriptionStatus));

              try {
                SyncEngine.shutdown();
                await SyncEngine.initialize();
                await fetchAllCases();
                await triggerSync();
                console.log('Session refreshed and sync re-initialized');
              } catch (syncError) {
                console.error('Sync re-initialization failed during session refresh:', syncError);
              }

              return updatedData;
            }
          } catch (error) {
            console.error('Failed to refresh session:', error);
          }
          return null;
        },
        removeSession: (clearAllData = false) => {
          initStarted.current = false;
          setIsAppReady(false);
          setInitStage('');
          setData({});
          setAuthenticated(false);
          setAuthDate('');

          // Shut down sync and close the database. The encrypted Dexie data stays on
          // disk; clearing the session DEK makes it unreadable until re-authentication.
          // PRF wraps in localStorage are safe to keep — useless without the passkey.
          SyncEngine.shutdown();
          closeDatabase();
          clearSessionDek();

          dispatch(ActionCreators.sessionEnded(clearAllData));
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return React.useContext(AuthContext) || defaultAuthContext;
}

export { AuthProvider };
